import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Paper,
  Box,
  Modal,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import ShortlistProCard from "./ShortlistProCard";
import { DotLottleLoader } from "../../../components/Loader/DotLottleLoader";
import { LoadMoreBtn } from "../../../components/Loader";
import IconSearch from "../../../assets/svg/search-icon-grey.svg";
import { NoDataView } from "../../../components/EmptyPanel/NoDataView";
import { IconUser } from "../../../utils/icons";

interface Expert {
  userid: number;
  avatar: string;
  displayName: string;
}

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  shortlist: any;
  refresh: any;
}

const LIMIT = 10;
export default function AddShortlistModal({
  open,
  setOpen,
  shortlist,
  refresh,
}: Props) {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [pros, setPros] = useState<Expert[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const initialExpertIds = React.useMemo(
    () => shortlist?.experts?.map((e: any) => e.userid) ?? [],
    [shortlist?.experts],
  );
  const [selectedExperts, setSelectedExperts] =
    useState<number[]>(initialExpertIds);

  const [isSaving, setIsSaving] = useState(false);

  const fetchExperts = async () => {
    if (!shortlist.id) return;
    setIsLoading(true);

    try {
      const response = await axiosInstant.get("/users/pros", {
        params: { page, limit: LIMIT, search: searchTerm, status: "Active" },
      });

      const proList =
        page === 1 ? response.data.rows : [...pros, ...response.data.rows];
      setHasMore(response.data.rows.length === LIMIT);
      setPros(proList);
    } catch (error) {
      console.error("Error fetching experts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setSearchTerm(e.target.value);
      setPage(1);
    }, 500);
    return () => clearTimeout(timeoutRef.current);
  };

  const handleLoadMore = () => {
    if (!isLoading && hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    if (open) {
      fetchExperts();
    }
  }, [open, page, searchTerm]);

  useEffect(() => {
    if (open && shortlist?.experts) {
      setSelectedExperts(
        shortlist.experts.map((expert: any) => expert.userid) || [],
      );
    }
  }, [open, shortlist?.experts]);

  const handleSelectExpert = (expertId: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedExperts((prev) => [...prev, expertId]);
    } else {
      setSelectedExperts((prev) => prev.filter((id) => id !== expertId));
    }
  };

  const handleUpdate = async () => {
    if (!shortlist?.id) return;

    setIsSaving(true);

    const expertsToAdd = selectedExperts.filter(
      (id) => !initialExpertIds.includes(id),
    );
    const expertsToRemove = initialExpertIds.filter(
      (id: number) => !selectedExperts.includes(id),
    );

    try {
      for (const expertId of expertsToAdd) {
        await axiosInstant.post(`/shortlists/${shortlist.id}/experts`, {
          userId: expertId,
        });
      }

      for (const expertId of expertsToRemove) {
        await axiosInstant.delete(
          `/shortlists/${shortlist.id}/experts/${expertId}`,
        );
      }

      toast.success("Shortlist updated successfully!");
      refresh();
      setOpen(false);
    } catch (error) {
      console.error("Error updating shortlist:", error);
      toast.error("Failed to update shortlist. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    initialExpertIds.length !== selectedExperts.length ||
    initialExpertIds.some((id: number) => !selectedExperts.includes(id)) ||
    selectedExperts.some((id) => !initialExpertIds.includes(id));

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <Box
        component={Paper}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: 'calc(100% - 24px)', md: '90vw', lg: 931 },
          maxWidth: 931,
          maxHeight: { xs: 'calc(100vh - 24px)', sm: '90vh' },
          bgcolor: 'var(--surface)',
          borderRadius: '12px',
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        <Box className="flex flex-col sticky top-0 bg-surface z-10 w-full">
          <Box className="flex justify-between items-center sticky top-0 bg-surface z-10 py-3 sm:py-4 w-full px-4 sm:px-6 gap-3">
            <div className="flex items-center gap-4 sm:gap-10 flex-1 min-w-0">
              <Typography
                sx={{
                  color: "var(--Base-1000, #000)",
                  fontFamily: '"Circular Std", sans-serif',
                  fontSize: { xs: 18, sm: 24 },
                  fontWeight: 900,
                  lineHeight: "132%",
                  whiteSpace: "nowrap",
                }}
              >
                Experts
              </Typography>

              <div className="flex-1 relative min-w-0">
                <img
                  className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
                  alt="search"
                  src={IconSearch}
                />
                <input
                  type="text"
                  className="w-full sm:max-w-[333px] h-[40px] px-[2.25rem] py-0 gap-2 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1"
                  id="search-experts"
                  placeholder="Search"
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <IconButton onClick={() => setOpen(false)} className="shrink-0">
              <CloseIcon />
            </IconButton>
          </Box>
          <hr className="border-border-token" />
        </Box>
        <Box className="w-full flex flex-col h-[500px] max-h-[80vh] overflow-y-auto">
          {((isLoading && page !== 1) || !isLoading) &&
            pros.map((pro) => (
              <ShortlistProCard
                key={pro.userid}
                pro={pro}
                isSelected={selectedExperts.includes(pro.userid)}
                isAlreadyAdded={initialExpertIds.includes(pro.userid)}
                onSelect={handleSelectExpert}
              />
            ))}
          {isLoading && page === 1 && (
            <Box className="flex justify-center mt-3">
              <DotLottleLoader />
            </Box>
          )}
          {!isLoading && pros.length === 0 && (
            <NoDataView
              Icon={IconUser}
              text="No experts found"
              panelClassName="!my-0 h-full"
            />
          )}
          {hasMore && (
            <LoadMoreBtn onClick={handleLoadMore} loading={isLoading} customClasses="!my-3" />
          )}
        </Box>
        {!isLoading && pros.length > 0 && (
          <Box className="flex items-center justify-end py-5 bg-surface px-6 sticky bottom-0 border-t border-border-token">
            <Button
              variant="contained"
              onClick={handleUpdate}
              size="large"
              disabled={isSaving || !hasChanges}
              sx={{
                backgroundColor: "var(--accent)",
                "&:hover": {
                  backgroundColor: "#8B6B3A",
                },
              }}
            >
              {isSaving ? "Updating..." : "Update"}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
